import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useNewsletter } from '../newsletter/NewsletterContext';
import { API_ENDPOINTS } from '../config';
import {
    buildAnnouncementSegments,
    isExternalAnnouncementUrl,
    isNewsletterUrl,
} from '../utils/announcementLinks';

const ROTATE_MS = 4500;
const FADE_MS = 280;
const MARQUEE_SPEED_PX_S = 50;
const MARQUEE_LOOP_PAUSE_MS = 700;
/** Match MUI `md` — marquee only below desktop. */
const MOBILE_MQ = '(max-width: 899.95px)';

const ICONS = {
    shipping: LocalShippingOutlinedIcon,
    mail: MailOutlineIcon,
};

/**
 * Announcement-bar links: typographically identical to surrounding text.
 * Must stay display:inline — MUI Box defaults to block, which lifts the line box.
 */
const linkSx = {
    display: 'inline',
    verticalAlign: 'baseline',
    color: 'inherit',
    font: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    textDecoration: 'underline',
    textDecorationColor: 'currentColor',
    textDecorationThickness: '1px',
    textUnderlineOffset: '0.15em',
    cursor: 'pointer',
    border: 0,
    outline: 'none',
    background: 'transparent',
    boxShadow: 'none',
    padding: 0,
    margin: 0,
    borderRadius: 0,
    transform: 'none',
    '&:hover, &:focus-visible': {
        display: 'inline',
        color: 'inherit',
        opacity: 1,
        background: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        border: 0,
        padding: 0,
        margin: 0,
        transform: 'none',
        textDecoration: 'underline',
        textDecorationColor: 'currentColor',
        textDecorationThickness: '1px',
        textUnderlineOffset: '0.15em',
    },
};

const messageTextBaseSx = {
    display: 'inline',
    fontSize: 'clamp(0.75rem, 1.8vw, 0.8rem)',
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
};

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    });

    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduced(mq.matches);
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);

    return reduced;
}

function useIsMobileAnnouncement() {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia(MOBILE_MQ).matches;
    });

    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia(MOBILE_MQ);
        const onChange = () => setIsMobile(mq.matches);
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);

    return isMobile;
}

function setAnnouncementChrome(visible) {
    try {
        const root = document.documentElement;
        if (visible) {
            root.style.removeProperty('--announcement-height');
            root.style.removeProperty('--navbar-border-radius');
        } else {
            root.style.setProperty('--announcement-height', '0px');
            root.style.setProperty('--navbar-border-radius', 'var(--header-radius)');
        }
    } catch (_) {
        /* ignore */
    }
}

function AnnouncementLink({ url, children, onNewsletter }) {
    if (isNewsletterUrl(url)) {
        return (
            <Box
                component="button"
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    onNewsletter?.();
                }}
                sx={{
                    appearance: 'none',
                    ...linkSx,
                }}
            >
                {children}
            </Box>
        );
    }

    if (isExternalAnnouncementUrl(url)) {
        return (
            <Box
                component="a"
                href={url}
                target={/^mailto:/i.test(url) ? undefined : '_blank'}
                rel={/^mailto:/i.test(url) ? undefined : 'noopener noreferrer'}
                sx={linkSx}
            >
                {children}
            </Box>
        );
    }

    return (
        <Box component={RouterLink} to={url || '/'} sx={linkSx}>
            {children}
        </Box>
    );
}

function MessageBody({ msg, isHebrew, openNewsletterModal, naturalWidth }) {
    const text = isHebrew
        ? (msg.text || '')
        : (msg.textEn || msg.text_en || msg.text || '');
    const Icon = msg.icon && ICONS[msg.icon] ? ICONS[msg.icon] : null;
    const isNewsletterAction = msg.action === 'newsletter';
    const segments = buildAnnouncementSegments(text, msg.links);
    const hasInlineLinks = segments.some((seg) => seg.type === 'link');

    /* On mobile, always use natural width so scrollWidth can detect overflow. */
    const textNode = (
        <Box
            component="span"
            data-announcement-text=""
            sx={{
                ...messageTextBaseSx,
                ...(naturalWidth
                    ? {
                        overflow: 'visible',
                        maxWidth: 'none',
                        textOverflow: 'clip',
                    }
                    : {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                    }),
            }}
        >
            {segments.map((seg, i) => (
                seg.type === 'link' ? (
                    <AnnouncementLink
                        key={i}
                        url={seg.url}
                        onNewsletter={openNewsletterModal}
                    >
                        {seg.value}
                    </AnnouncementLink>
                ) : (
                    <React.Fragment key={i}>{seg.value}</React.Fragment>
                )
            ))}
        </Box>
    );

    const row = (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: naturalWidth ? 'flex-start' : 'center',
                gap: '0.4rem',
                height: '100%',
                minHeight: 'var(--announcement-height)',
                maxWidth: naturalWidth ? 'none' : '100%',
                width: naturalWidth ? 'max-content' : undefined,
                flexShrink: 0,
                color: 'inherit',
                lineHeight: 1,
                direction: isHebrew ? 'rtl' : 'ltr',
                whiteSpace: 'nowrap',
            }}
        >
            {Icon ? (
                <Icon
                    sx={{
                        fontSize: 'clamp(0.85rem, 2vw, 1rem)',
                        flexShrink: 0,
                        display: 'block',
                        lineHeight: 1,
                    }}
                    aria-hidden
                />
            ) : null}
            {textNode}
        </Box>
    );

    if (isNewsletterAction) {
        return (
            <Box
                component="button"
                type="button"
                onClick={() => openNewsletterModal()}
                sx={{
                    appearance: 'none',
                    display: naturalWidth ? 'inline-flex' : 'flex',
                    alignItems: 'center',
                    justifyContent: naturalWidth ? 'flex-start' : 'center',
                    height: '100%',
                    minHeight: 'var(--announcement-height)',
                    maxWidth: naturalWidth ? 'none' : '100%',
                    width: naturalWidth ? 'max-content' : undefined,
                    flexShrink: 0,
                    margin: 0,
                    padding: 0,
                    border: 0,
                    borderRadius: 0,
                    background: 'transparent',
                    boxShadow: 'none',
                    color: 'inherit',
                    font: 'inherit',
                    lineHeight: 1,
                    cursor: 'pointer',
                    outline: 'none',
                    transform: 'none',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover, &:focus-visible': {
                        outline: 'none',
                        transform: 'none',
                    },
                    ...(!hasInlineLinks
                        ? {
                            '& [data-announcement-text]': {
                                textDecoration: 'underline',
                                textDecorationColor: 'currentColor',
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '0.15em',
                            },
                        }
                        : {}),
                }}
            >
                {row}
            </Box>
        );
    }

    return row;
}

export default function AnnouncementBar() {
    const { isHebrew } = useLanguage();
    const { openNewsletterModal } = useNewsletter();
    const reducedMotion = usePrefersReducedMotion();
    const isMobile = useIsMobileAnnouncement();
    const [messages, setMessages] = useState(null);
    const [index, setIndex] = useState(0);
    const [fadeIn, setFadeIn] = useState(true);
    const [marquee, setMarquee] = useState(false);
    const [measureTick, setMeasureTick] = useState(0);
    const pausedRef = useRef(false);
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const animRef = useRef(null);
    const rotateTimerRef = useRef(null);
    const playGenRef = useRef(0);
    const listRef = useRef([]);
    const isHebrewRef = useRef(isHebrew);

    isHebrewRef.current = isHebrew;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(API_ENDPOINTS.announcements);
                const data = await res.json().catch(() => ({}));
                if (cancelled) return;
                const items = res.ok && data.success && Array.isArray(data.items) ? data.items : [];
                setMessages(items);
            } catch (_) {
                if (!cancelled) setMessages([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const list = Array.isArray(messages) ? messages : [];
    listRef.current = list;
    const showBar = list.length > 0;
    const msg = showBar ? (list[index] || list[0]) : null;

    useEffect(() => {
        setAnnouncementChrome(showBar);
        return () => setAnnouncementChrome(false);
    }, [showBar]);

    useEffect(() => {
        setIndex(0);
        setFadeIn(true);
        setMarquee(false);
        setMeasureTick((n) => n + 1);
    }, [list.length, isHebrew]);

    const clearTimersAndAnim = () => {
        if (rotateTimerRef.current != null) {
            window.clearTimeout(rotateTimerRef.current);
            rotateTimerRef.current = null;
        }
        if (animRef.current) {
            try {
                animRef.current.cancel();
            } catch (_) {
                /* ignore */
            }
            animRef.current = null;
        }
    };

    useLayoutEffect(() => {
        if (!showBar || !msg) return undefined;

        clearTimersAndAnim();
        const gen = ++playGenRef.current;
        const items = listRef.current;

        const advance = () => {
            setFadeIn(false);
            rotateTimerRef.current = window.setTimeout(() => {
                setIndex((i) => (i + 1) % Math.max(items.length, 1));
                setFadeIn(true);
                setMarquee(false);
            }, FADE_MS);
        };

        const scheduleStaticRotate = () => {
            if (items.length <= 1) return;
            rotateTimerRef.current = window.setTimeout(() => {
                if (gen !== playGenRef.current) return;
                if (pausedRef.current) {
                    scheduleStaticRotate();
                    return;
                }
                advance();
            }, ROTATE_MS);
        };

        const runMarquee = (textWidth, barWidth) => {
            const track = trackRef.current;
            if (!track || typeof track.animate !== 'function') {
                scheduleStaticRotate();
                return;
            }

            /*
             * RTL (Hebrew): enter from the left, exit to the right.
             * LTR: enter from the right, exit to the left.
             */
            const rtl = isHebrewRef.current;
            const fromX = rtl ? -textWidth : barWidth;
            const toX = rtl ? barWidth : -textWidth;
            const distance = textWidth + barWidth;
            const durationMs = Math.max(1200, (distance / MARQUEE_SPEED_PX_S) * 1000);

            track.style.transform = `translateX(${fromX}px)`;

            const animation = track.animate(
                [
                    { transform: `translateX(${fromX}px)` },
                    { transform: `translateX(${toX}px)` },
                ],
                {
                    duration: durationMs,
                    easing: 'linear',
                    fill: 'forwards',
                }
            );
            animRef.current = animation;

            const afterPass = () => {
                if (gen !== playGenRef.current) return;

                const continueAfterPause = () => {
                    if (gen !== playGenRef.current) return;
                    if (pausedRef.current) {
                        rotateTimerRef.current = window.setTimeout(continueAfterPause, 200);
                        return;
                    }
                    if (items.length <= 1) {
                        rotateTimerRef.current = window.setTimeout(() => {
                            if (gen !== playGenRef.current) return;
                            setMeasureTick((n) => n + 1);
                        }, MARQUEE_LOOP_PAUSE_MS);
                    } else {
                        advance();
                    }
                };

                continueAfterPause();
            };

            animation.onfinish = () => {
                animRef.current = null;
                afterPass();
            };
            animation.oncancel = () => {
                animRef.current = null;
            };
        };

        const measureAndPlay = () => {
            if (gen !== playGenRef.current) return;
            const viewport = viewportRef.current;
            const track = trackRef.current;
            if (!viewport || !track) return;

            track.style.transform = 'none';
            const textWidth = Math.ceil(track.scrollWidth);
            const barWidth = Math.ceil(viewport.clientWidth);
            const overflows = isMobile && !reducedMotion && textWidth > barWidth + 1;

            setMarquee(overflows);

            if (overflows) {
                requestAnimationFrame(() => {
                    if (gen !== playGenRef.current) return;
                    const tw = Math.ceil(track.scrollWidth);
                    const bw = Math.ceil(viewport.clientWidth);
                    runMarquee(tw, bw);
                });
            } else if (!reducedMotion && items.length > 1) {
                scheduleStaticRotate();
            }
        };

        const raf = requestAnimationFrame(measureAndPlay);

        return () => {
            cancelAnimationFrame(raf);
            if (gen === playGenRef.current) {
                clearTimersAndAnim();
            }
        };
    }, [showBar, msg, index, isMobile, reducedMotion, measureTick]);

    /*
     * Remeasure only when the layout *width* changes (orientation / real resize).
     * Mobile browsers fire resize on scroll when the URL bar shows/hides (height-only);
     * reacting to those was restarting marquee/rotation and felt like the messages “reset”.
     */
    useEffect(() => {
        let lastWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
        let debounceTimer = null;

        const onResize = () => {
            const nextWidth = window.innerWidth;
            if (Math.abs(nextWidth - lastWidth) < 2) return;
            lastWidth = nextWidth;
            if (debounceTimer != null) window.clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(() => {
                clearTimersAndAnim();
                setMarquee(false);
                /* Keep current index / fade — only remasure playback for the active message */
                setMeasureTick((n) => n + 1);
            }, 150);
        };

        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
            if (debounceTimer != null) window.clearTimeout(debounceTimer);
        };
    }, []);

    if (!showBar) return null;

    return (
        <Box
            component="aside"
            role="region"
            aria-label={isHebrew ? 'הודעות' : 'Announcements'}
            data-announcement-bar="1"
            onMouseEnter={() => {
                pausedRef.current = true;
                try { animRef.current?.pause(); } catch (_) { /* ignore */ }
            }}
            onMouseLeave={() => {
                pausedRef.current = false;
                try { animRef.current?.play(); } catch (_) { /* ignore */ }
            }}
            sx={{
                position: 'relative',
                width: '100%',
                height: 'var(--announcement-height)',
                flexShrink: 0,
                backgroundColor: 'var(--brand-orange)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 'clamp(0.5rem, 2.5vw, 1.25rem)',
                py: 0,
                boxSizing: 'border-box',
                margin: 0,
                borderRadius: 0,
                boxShadow: 'none',
                border: 'none',
            }}
        >
            <Box
                ref={viewportRef}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: marquee ? 'flex-start' : 'center',
                    height: '100%',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    lineHeight: 1,
                    opacity: fadeIn || reducedMotion ? 1 : 0,
                    transform: reducedMotion
                        ? 'none'
                        : fadeIn
                            ? 'translateY(0)'
                            : 'translateY(0.2rem)',
                    transition: reducedMotion
                        ? 'none'
                        : `opacity ${FADE_MS / 1000}s ease, transform ${FADE_MS / 1000}s ease`,
                }}
            >
                <Box
                    ref={trackRef}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: '100%',
                        flexShrink: 0,
                        willChange: marquee ? 'transform' : 'auto',
                        backfaceVisibility: 'hidden',
                    }}
                >
                    <MessageBody
                        msg={msg}
                        isHebrew={isHebrew}
                        openNewsletterModal={openNewsletterModal}
                        naturalWidth={isMobile || marquee}
                    />
                </Box>
            </Box>
        </Box>
    );
}
